import { FormattedText } from "client/Components";
import { DraftEmailAttachment } from "client/Api/Email/respondToEmail";
import {
	AlertManager,
	useAlerts,
	useSynchronizedRef
} from "client/Hooks";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	convertToLocationModel
} from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import {
	EmailAttachmentResultModel,
	InsertAttachmentFromDmsResult,
	MailMessageLocationModel
} from "client/Api/Email/common";
import { partition, zip } from "client_legacy/ndaw/arrayHelpers";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	ReadonlyRefObject,
	useCallback,
	useMemo
} from "react";
import {
	AttachmentItem,
	AttachmentKind,
	AttachmentStatus,
	EnqueueAttachmentUpload
} from "./common";
import { FileSelection } from "client/Features/Documents/ItemPicker/ItemPicker";
import { SharePointEmailAttachmentModel, uploadSharePointAttachments } from "client/Api/Email/sharePointAttachments";
import { uploadWebDavAttachments, WebDavEmailAttachment } from "client/Api/Email/webDavAttachments";

export type UploadAttachmentsFromDmsOptions = {
	draft: MailMessageLocationModel;
	sizeLimit: number;
	totalVirtualSize: number;
	enqueueUpload: EnqueueAttachmentUpload;
	onAddAttachment(attachment: AttachmentItem): void;
	onAttachmentUploadFinish(uid: string, model: DraftEmailAttachment): void;
	onRemoveAttachment(uid: string): void;
};

export type UploadAttachmentsFromDmsHandlers = {
	uploadAttachmentsFromDms(selection: FileSelection): Promise<void>;
};

export function useUploadAttachmentsFromDms(
	options: UploadAttachmentsFromDmsOptions
): UploadAttachmentsFromDmsHandlers {
	const {
		sizeLimit,
		enqueueUpload,
		onAddAttachment,
		onAttachmentUploadFinish,
		onRemoveAttachment,
		totalVirtualSize,
		draft
	} = options;

	const totalVirtualSizeRef: ReadonlyRefObject<number> = useSynchronizedRef<number>(totalVirtualSize);
	const alertManager: AlertManager = useAlerts();

	const processInsertAttachmentResponse = useCallback(
		(
			results: readonly EmailAttachmentResultModel[],
			attachmentUids: string[]
		): void => {
			const fileNamesWithNoPermission: string[] = [];
			const fileNamesThatAreToBigAfterUpload: string[] = [];
			const folderNames: string[] = [];

			for (const [emailAttachmentResult, uid] of zip(results, attachmentUids)) {
				switch (emailAttachmentResult.state) {
					case InsertAttachmentFromDmsResult.Ok:
						onAttachmentUploadFinish(uid, emailAttachmentResult.attachment);
						break;
					case InsertAttachmentFromDmsResult.DoNotEnoughPermissions:
						fileNamesWithNoPermission.push(emailAttachmentResult.name);
						onRemoveAttachment(uid);
						break;
					case InsertAttachmentFromDmsResult.AttachmentsTooBig:
						fileNamesThatAreToBigAfterUpload.push(emailAttachmentResult.name);
						onRemoveAttachment(uid);
						break;
					case InsertAttachmentFromDmsResult.InsertFolderDoNotSupported:
						folderNames.push(emailAttachmentResult.name);
						onRemoveAttachment(uid);
						break;
					default:
						never(emailAttachmentResult.state);
				}
			}

			const hasPermissionErrors: boolean = fileNamesWithNoPermission.length > 0;
			const hasFileSizeErrors: boolean = fileNamesThatAreToBigAfterUpload.length > 0;
			const hasUploadFolderErrors: boolean = folderNames.length > 0;

			const hasErrors: boolean = hasPermissionErrors
				|| hasFileSizeErrors
				|| hasUploadFolderErrors;

			if (hasErrors) {
				alertManager.showError(
					<>
						{hasPermissionErrors && (
							<>
								<FormattedText
									text={localize("_NoPermissionForTheseFiles")}
									thingsToReplace={[{
										what: "{0}",
										filler: fileNamesWithNoPermission.join(", ")
									}]}
								/>
								<br />
							</>
						)}
						{hasFileSizeErrors && (
							<>
								<FormattedText
									text={localize("_TheseAttachmentsAreToBigToAdd")}
									thingsToReplace={[{
										what: "{0}",
										filler: fileNamesThatAreToBigAfterUpload.join(", ")
									}]}
								/>
								<br />
							</>
						)}
						{hasUploadFolderErrors && (
							<FormattedText
								text={localize("_TheseFoldersWhereDetectedWhenAddingAttachments")}
								thingsToReplace={[{
									what: "{0}",
									filler: folderNames.join(", ")
								}]}
							/>
						)}
					</>
				);
			}
		},
		[
			alertManager,
			onAttachmentUploadFinish,
			onRemoveAttachment
		]
	);

	const uploadAttachmentsFromDms = useCallback(
		async (selection: FileSelection): Promise<void> => {
			let models: { attachment: AttachmentItem; file: InsertAttachmentModel; }[];

			switch (selection.kind) {
				case "sharepoint":
					models = selection.items
						.map((item) => {
							return {
								file: {
									fileId: item.id,
									location: convertToLocationModel(item.location)
								},
								attachment: {
									uid: newGuid(),
									kind: AttachmentKind.Normal,
									name: item.name,
									size: item.itemType === DocumentItemType.File ? item.size : 0,
									status: AttachmentStatus.Uploading
								}
							};
						});
					break;
				case "webdav":
					models = selection.items
						.map((item) => {
							return {
								file: {
									fileId: item.id,
									serverId: item.serverId
								},
								attachment: {
									uid: newGuid(),
									kind: AttachmentKind.Normal,
									name: item.name,
									size: item.size,
									status: AttachmentStatus.Uploading
								}
							};
						});
					break;
				default:
					throw new Error("Unknown selection kind");
			}

			const [withinLimits, outsideLimits] = partition(
				models,
				(model) => model.attachment.size + totalVirtualSizeRef.current <= sizeLimit
			);

			if (outsideLimits.length > 0) {
				const nameOfFilesWhichDontFit: string[] = outsideLimits.map((x) => x.attachment.name);
				const message: string = localize("_TheseAttachmentsAreToBigToAdd")
					.replace("{0}", nameOfFilesWhichDontFit.join(", "));

				alertManager.showError(message);
			}

			// if there is no data for attachments just return since ajax call does not needs to be called
			// when there are no attachments to be added
			if (withinLimits.length === 0) {
				return;
			}

			withinLimits.forEach((x) => onAddAttachment(x.attachment));
			const attachmentUids: string[] = withinLimits.map((x) => x.attachment.uid);

			try {
				let result: readonly EmailAttachmentResultModel[];

				switch (selection.kind) {
					case "sharepoint":
						result = await enqueueUpload(
							() => uploadSharePointAttachments({
								draft,
								attachments: withinLimits.map(x => x.file as SharePointEmailAttachmentModel)
							})
						);
						break;
					case "webdav":
						result = await enqueueUpload(
							() => uploadWebDavAttachments({
								draft,
								attachments: withinLimits.map(x => x.file as WebDavEmailAttachment)
							})
						);
						break;
					default:
						never(selection);
				}

				processInsertAttachmentResponse(result, attachmentUids);
			} catch(e) {
				attachmentUids.forEach((uid) => onRemoveAttachment(uid));
				alertManager.showError(localize("_NoAttachmentsUploaded"));
			}
		},
		[
			alertManager,
			draft,
			enqueueUpload,
			onAddAttachment,
			onRemoveAttachment,
			processInsertAttachmentResponse,
			sizeLimit,
			totalVirtualSizeRef
		]
	);

	return useMemo(
		(): UploadAttachmentsFromDmsHandlers => ({
			uploadAttachmentsFromDms
		}),
		[uploadAttachmentsFromDms]
	);
}

type InsertAttachmentModel = SharePointEmailAttachmentModel | WebDavEmailAttachment;